import React, { useCallback } from 'react'
import { Link } from 'react-router'
import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { MessageAttachments } from '@components/MessageAttachments/MessageAttachments'
import { EmbeddedAttachmentsContext } from '@components/MessageAttachments/MessageAttachmentsContext'
import { useFocusMessage } from '@components/MessageTimeline/hooks/useFocusItem'
import { useTimelineContext } from '@components/MessageTimeline/hooks/useTimelineContext'
import { isChannelMessage } from '@components/MessageTimeline/util/getEventsByDate'
import { TimelinesViewModel } from '@towns-protocol/sdk'
import { Box, Text } from '@ui'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import { useThrottledTimelineStore, useUserLookupContext } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { getShortTextFromMarkdown } from 'utils/getShortTextFromMarkdown'
import { htmlToText } from 'workers/data_transforms'

type Props = {
    eventId: string
}

export const QuotedMessage = (props: Props) => {
    const { eventId } = props
    const { isTouch } = useDevice()
    const timelineContext = useTimelineContext()
    const { createLink } = useCreateLink()
    const { lookupUser } = useUserLookupContext()
    const { refocusItem } = useFocusMessage((state) => state.actions)

    // Get the specific event directly from the timeline store
    const event = useThrottledTimelineStore(
        useCallback(
            (state: TimelinesViewModel) => {
                const channelId = timelineContext?.channelId
                if (!channelId) {
                    return null
                }

                const timeline = state.timelines[channelId] ?? []
                return timeline.find((e) => e.eventId === eventId) || null
            },
            [eventId, timelineContext?.channelId],
        ),
    )

    if (!event) {
        return <Box>Quoted message not found</Box>
    } else if (!isChannelMessage(event)) {
        return <Box>No preview available</Box>
    }

    const displayName = getPrettyDisplayName(lookupUser(event.sender.id))

    const threadLink =
        createLink({
            ...(timelineContext.spaceId ? { spaceId: timelineContext.spaceId } : {}),
            ...(timelineContext.spaceId
                ? // direct replies to slash commands
                  // if there's a spaceId, then we should go to channel route
                  { channelId: timelineContext.channelId }
                : // not sure when this applies?
                  { messageId: timelineContext.channelId }),
        }) + `#${eventId}`

    const attachments = isChannelMessage(event) ? event.content.attachments : undefined

    const handleClick = (e: React.MouseEvent) => {
        // Check if we're already at this hash - if so, prevent navigation and just refocus
        const currentHash = window.location.hash.slice(1) // remove #
        if (currentHash === eventId) {
            e.preventDefault()
        }
        // Always call refocusItem for the scroll behavior
        refocusItem(eventId, 'forced')
    }

    return (
        <Box gap="sm">
            <Link to={threadLink} onClick={handleClick}>
                <Box
                    shrink
                    hoverable
                    background="level2"
                    maxWidth={isTouch ? '300' : '500'}
                    padding="paragraph"
                    rounded="md"
                    width="fit-content"
                    gap="paragraph"
                >
                    <Box horizontal gap="xs" color="gray2">
                        <Box display="inline-block" width="paragraph" insetTop="2">
                            <AvatarWithoutDot userId={event.sender.id} size="avatar_paragraph" />
                        </Box>
                        <Text
                            as="span"
                            display="inline"
                            fontWeight="medium"
                            size="sm"
                            color="gray1"
                        >
                            {displayName}
                        </Text>

                        <Text truncate as="span" display="inline" size="sm">
                            {getShortTextFromMarkdown(htmlToText(event.content.body || '') || '')}
                        </Text>
                    </Box>
                    <EmbeddedAttachmentsContext.Provider
                        value={{ isMessageAttachementContext: true }}
                    >
                        {attachments && attachments.length > 0 && (
                            <MessageAttachments attachments={attachments} eventId={eventId} />
                        )}
                    </EmbeddedAttachmentsContext.Provider>
                </Box>
            </Link>
        </Box>
    )
}
